import React, { useEffect, useState } from "react";
import moment from "moment";
import _ from "lodash";
import { breaks } from "../../utils/Helpers";
import { addbreak, deleteBreak, updateBreak } from "../../services";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, Coffee, Plus, Trash2, Edit3, Save, RotateCcw, Activity, Timer, ChevronRight, AlertCircle, CheckCircle2, LayoutDashboard, History } from "lucide-react";

export default function DatamodalAdmin({
  Breaks,
  date,
  MemberId,
  fetchTime,
  offset,
}) {
  const [modal, setModal] = useState(false);
  const [editableBreaks, setEditableBreaks] = useState(Breaks);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBreakId, setSelectedBreakId] = useState(null);

  const [AddBreakData, SetAddBreakData] = useState({
    BreakReason: 1,
    BreakType: 1,
    StartTime: "",
    EndTime: "",
  });
  const [editRowId, setEditRowId] = useState(null);
  const [editData, setEditData] = useState({});

  const [errMsg, setErrMsg] = useState({
    err: false,
    msg: "",
    color: "red",
  });

  const toggleModal = () => {
    setModal(!modal);
    document.body.classList.toggle("active-modal", !modal);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData({
      ...editData,
      [name]: value,
    });
  };

  const allReasons = Object.values(breaks).flatMap((breakType) =>
    breakType.reasons.map((reason) => ({
      id: reason.id,
      label: reason.label,
    }))
  );

  const transformedBreaks = Object.keys(breaks).reduce((acc, key) => {
    const breakItem = breaks[key];
    acc[breakItem.id] = key;
    return acc;
  }, {});

  const getBreakReasonLabel = (breakReasonId) => {
    const foundReason = allReasons.find((reason) => reason.id === breakReasonId);
    return foundReason ? foundReason.label : "Unknown";
  };

  const handleBreakSubmit = async () => {
    if (_.isEmpty(AddBreakData.StartTime) || _.isEmpty(AddBreakData.EndTime)) {
      setErrMsg({ err: true, msg: "Please fill all fields", color: "red" });
      return;
    }
    setIsLoading(true);
    try {
      const breakdata = {
        MemberId: MemberId,
        BreakType: Number(AddBreakData.BreakType),
        BreakReason: Number(AddBreakData.BreakReason),
        Comment: "Manually Added",
        StartTime: moment(`${date} ${AddBreakData.StartTime}`, "YYYY/MM/DD HH:mm").toISOString(),
        EndTime: moment(`${date} ${AddBreakData.EndTime}`, "YYYY/MM/DD HH:mm").toISOString(),
      };
      await addbreak(breakdata);
      await fetchTime(offset);
      SetAddBreakData({ BreakReason: 1, BreakType: 1, StartTime: "", EndTime: "" });
      setErrMsg({ err: true, msg: "Break added successfully", color: "green" });
      setTimeout(() => setErrMsg({ err: false, msg: "", color: "red" }), 3000);
    } catch (error) {
      setErrMsg({ err: true, msg: "Error adding break", color: "red" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateBreak = async () => {
    if (_.isEmpty(editData?.StartTime) || _.isEmpty(editData?.EndTime)) {
      setErrMsg({ err: true, msg: "Please fill all fields", color: "red" });
      return;
    }
    setIsLoading(true);
    try {
      const breakData = {
        Id: editData?.Id,
        BreakReason: Number(editData?.BreakReason),
        BreakType: Number(editData?.BreakType),
        StartTime: moment(`${date} ${editData.StartTime}`, "YYYY/MM/DD HH:mm").toISOString(),
        EndTime: moment(`${date} ${editData.EndTime}`, "YYYY/MM/DD HH:mm").toISOString(),
      };
      await updateBreak(breakData);
      await fetchTime(offset);
      setEditRowId(null);
      setEditData({});
      setConfirmModal(false);
      setErrMsg({ err: true, msg: "Break updated successfully", color: "green" });
      setTimeout(() => setErrMsg({ err: false, msg: "", color: "red" }), 3000);
    } catch (error) {
      setErrMsg({ err: true, msg: "Error updating break", color: "red" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteBreak = async (Id) => {
    setIsLoading(true);
    try {
      await deleteBreak(Id);
      await fetchTime(offset);
      setIsModalOpen(false);
      setSelectedBreakId(null);
      setErrMsg({ err: true, msg: "Break deleted successfully", color: "green" });
      setTimeout(() => setErrMsg({ err: false, msg: "", color: "red" }), 3000);
    } catch (error) {
      setErrMsg({ err: true, msg: "Error deleting break", color: "red" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setEditableBreaks(Breaks);
  }, [Breaks]);

  const calculateDiff = (start, end) => {
    const s = moment(start, "HH:mm");
    const e = moment(end, "HH:mm");
    const duration = moment.duration(e.diff(s));
    return `${Math.floor(duration.asHours())}h ${duration.minutes()}m`;
  };

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.2);
          border-radius: 10px;
          border: 2px solid transparent;
          background-clip: content-box;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.4);
          background-clip: content-box;
        }
      `}</style>
      <button
        onClick={toggleModal}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-bold rounded-lg hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all text-[11px] group shadow-sm border border-blue-100/50 dark:border-blue-500/10"
      >
        <Edit3 className="w-3.5 h-3.5 group-hover:rotate-12 transition-transform" />
        Manage
      </button>

      <AnimatePresence>
        {modal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleModal}
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative bg-white dark:bg-[#0A0A0A] rounded-[2.5rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.5)] w-full max-w-4xl max-h-[92vh] overflow-hidden border border-gray-100 dark:border-white/10 flex flex-col"
            >
              {/* Premium Header */}
              <div className="flex items-center justify-between p-8 bg-white dark:bg-[#0A0A0A] border-b dark:border-white/5 relative z-10">
                <div className="flex items-center gap-5">
                  <div className="p-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg shadow-blue-500/20 group relative overflow-hidden">
                    <Coffee className="w-8 h-8 text-white relative z-10" />
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <LayoutDashboard className="w-4 h-4 text-blue-500" />
                      <span className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em]">Administration</span>
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-black dark:text-white leading-tight tracking-tight">Break Session Manager</h2>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <AnimatePresence>
                    {errMsg.err && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9, x: 20 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.9, x: 20 }}
                        className={`flex items-center gap-2.5 px-5 py-2.5 rounded-2xl text-[11px] font-black border uppercase tracking-wider ${errMsg.color === "green"
                          ? "bg-green-500/10 text-green-500 border-green-500/20"
                          : "bg-red-500/10 text-red-500 border-red-500/20"
                          }`}
                      >
                        {errMsg.color === "green" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                        {errMsg.msg}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <button
                    onClick={toggleModal}
                    className="p-3 bg-gray-50 dark:bg-white/5 hover:bg-red-500 hover:text-white dark:hover:bg-red-500 rounded-2xl text-gray-400 transition-all duration-300 transform active:scale-95 group"
                  >
                    <X size={24} className="group-hover:rotate-180 transition-transform duration-500" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto p-8 custom-scrollbar relative">
                {/* Floating Glow effects */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/5 blur-[120px] rounded-full -z-10" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-500/5 blur-[120px] rounded-full -z-10" />

                {/* Add New Break Entry Interface */}
                <div className="mb-10 bg-white dark:bg-white/[0.02] rounded-[2rem] border border-gray-100 dark:border-white/5 p-8 shadow-sm group">
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-50 dark:bg-blue-500/10 rounded-xl flex items-center justify-center">
                        <Plus className="w-5 h-5 text-blue-500" />
                      </div>
                      <h3 className="text-base font-black dark:text-gray-100 tracking-tight">Register Temporary Session</h3>
                    </div>
                    <div className="h-px flex-1 mx-8 bg-gray-50 dark:bg-white/5" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 items-end">
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2 ml-1">
                        <Clock className="w-3 h-3 text-gray-400" />
                        <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Break In</label>
                      </div>
                      <input
                        type="time"
                        value={AddBreakData.StartTime}
                        onChange={(e) => SetAddBreakData({ ...AddBreakData, StartTime: e.target.value })}
                        className="w-full h-12 bg-gray-50/50 dark:bg-black/40 border border-gray-100 dark:border-white/10 rounded-2xl px-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/30 transition-all"
                      />
                    </div>
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2 ml-1">
                        <Clock className="w-3 h-3 text-gray-400" />
                        <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Break Out</label>
                      </div>
                      <input
                        type="time"
                        value={AddBreakData.EndTime}
                        onChange={(e) => SetAddBreakData({ ...AddBreakData, EndTime: e.target.value })}
                        className="w-full h-12 bg-gray-50/50 dark:bg-black/40 border border-gray-100 dark:border-white/10 rounded-2xl px-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/30 transition-all"
                      />
                    </div>
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2 ml-1">
                        <Activity className="w-3 h-3 text-gray-400" />
                        <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Type</label>
                      </div>
                      <select
                        value={AddBreakData.BreakType}
                        onChange={(e) => SetAddBreakData({ ...AddBreakData, BreakType: e.target.value })}
                        className="w-full h-12 bg-gray-50/50 dark:bg-black/40 border border-gray-100 dark:border-white/10 rounded-2xl px-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
                      >
                        {Object.entries(transformedBreaks).map(([id, type]) => (
                          <option key={id} value={id}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2 ml-1">
                        <Edit3 className="w-3 h-3 text-gray-400" />
                        <label className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Category</label>
                      </div>
                      <select
                        value={AddBreakData.BreakReason}
                        onChange={(e) => SetAddBreakData({ ...AddBreakData, BreakReason: e.target.value })}
                        className="w-full h-12 bg-gray-50/50 dark:bg-black/40 border border-gray-100 dark:border-white/10 rounded-2xl px-4 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
                      >
                        {allReasons.map((reason) => (
                          <option key={reason.id} value={reason.id}>{reason.label}</option>
                        ))}
                      </select>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleBreakSubmit}
                      disabled={isLoading}
                      className="h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black text-[13px] uppercase tracking-wider flex items-center justify-center gap-3 transition-all shadow-xl shadow-blue-500/20 disabled:opacity-50"
                    >
                      {isLoading ? <RotateCcw size={18} className="animate-spin" /> : "Add break"}
                    </motion.button>
                  </div>
                </div>

                {/* Logged Sessions Feed */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between px-2">
                    <div className="flex items-center gap-3">
                      <History className="w-5 h-5 text-gray-400" />
                      <h3 className="text-sm font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">Session History</h3>
                    </div>
                    <div className="flex -space-x-2">
                      <div className="w-8 h-8 rounded-full bg-blue-500/10 border-2 border-white dark:border-[#0A0A0A] flex items-center justify-center text-[10px] font-black text-blue-500">{editableBreaks.length}</div>
                    </div>
                  </div>

                  {!_.isEmpty(editableBreaks) ? (
                    <div className="grid grid-cols-1 gap-4">
                      {editableBreaks.map((times, index) => (
                        <motion.div
                          key={times.Id}
                          initial={{ opacity: 0, scale: 0.98, y: 10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          transition={{ delay: index * 0.04 }}
                          className={`group p-6 rounded-[2rem] border border-gray-100 dark:border-white/5 transition-all duration-300 ${editRowId === times.Id
                            ? "bg-blue-500/[0.03] border-blue-500/30 ring-4 ring-blue-500/5 shadow-inner"
                            : "bg-white dark:bg-white/[0.01] hover:bg-gray-50 dark:hover:bg-white/[0.03] hover:shadow-xl dark:hover:shadow-none"
                            }`}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                            {editRowId === times.Id ? (
                              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-6">
                                <div className="space-y-2">
                                  <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest ml-1">Break In</span>
                                  <input type="time" name="StartTime" value={editData.StartTime} onChange={handleInputChange} className="w-full h-11 bg-white dark:bg-black/60 border border-gray-200 dark:border-white/20 rounded-2xl px-4 text-sm font-bold dark:text-white focus:ring-2 focus:ring-blue-500/30" />
                                </div>
                                <div className="space-y-2">
                                  <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest ml-1">Break Out</span>
                                  <input type="time" name="EndTime" value={editData.EndTime} onChange={handleInputChange} className="w-full h-11 bg-white dark:bg-black/60 border border-gray-200 dark:border-white/20 rounded-2xl px-4 text-sm font-bold dark:text-white focus:ring-2 focus:ring-blue-500/30" />
                                </div>
                                <div className="space-y-2">
                                  <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest ml-1">Type</span>
                                  <select name="BreakType" value={editData.BreakType} onChange={handleInputChange} className="w-full h-11 bg-white dark:bg-black/60 border border-gray-200 dark:border-white/20 rounded-2xl px-4 text-sm font-bold dark:text-white">
                                    {Object.entries(transformedBreaks).map(([id, type]) => (<option key={id} value={id}>{type}</option>))}
                                  </select>
                                </div>
                                <div className="space-y-2">
                                  <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest ml-1">Reason</span>
                                  <select name="BreakReason" value={editData.BreakReason} onChange={handleInputChange} className="w-full h-11 bg-white dark:bg-black/60 border border-gray-200 dark:border-white/20 rounded-2xl px-4 text-sm font-bold dark:text-white">
                                    {allReasons.map((reason) => (<option key={reason.id} value={reason.id}>{reason.label}</option>))}
                                  </select>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-10">
                                <div className="flex items-center gap-6">
                                  <div className="flex flex-col items-center">
                                    <span className="text-base font-black text-green-600 dark:text-green-500 tracking-tighter">
                                      {moment(times.StartTime).format("h:mm A")}
                                    </span>
                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Break In</span>
                                  </div>
                                  <div className="p-2 bg-gray-50 dark:bg-white/5 rounded-full">
                                    <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-700" />
                                  </div>
                                  <div className="flex flex-col items-center">
                                    <span className={`text-base font-black tracking-tighter ${times.EndTime ? "text-gray-800 dark:text-gray-200" : "text-blue-500 italic animate-pulse"}`}>
                                      {times.EndTime ? moment(times.EndTime).format("h:mm A") : "Active"}
                                    </span>
                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-1">Break Out</span>
                                  </div>
                                </div>

                                <div className="h-10 w-px bg-gray-100 dark:bg-white/10 hidden md:block" />

                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`w-1.5 h-1.5 rounded-full ${times.BreakType == 1 ? "bg-amber-500" : "bg-purple-500"}`} />
                                    <span className="text-[10px] font-black text-amber-500/80 dark:text-amber-500 uppercase tracking-widest">{transformedBreaks[times?.BreakType]}</span>
                                  </div>
                                  <div className="text-lg font-black text-gray-900 dark:text-gray-100 leading-none">{getBreakReasonLabel(times?.BreakReason)}</div>
                                </div>
                              </div>
                            )}

                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2.5 px-5 py-3 bg-gray-50/50 dark:bg-white/[0.04] rounded-2xl border border-gray-100 dark:border-white/5">
                                <Timer className="w-4 h-4 text-blue-500" />
                                <span className="text-[15px] font-black text-gray-800 dark:text-gray-200 tracking-tight">
                                  {editRowId === times.Id && editData.StartTime && editData.EndTime
                                    ? calculateDiff(editData.StartTime, editData.EndTime)
                                    : `${times.TotalHours}h ${times.TotalMins}m`
                                  }
                                </span>
                              </div>

                              <div className="flex items-center gap-2.5">
                                {editRowId === times.Id ? (
                                  <div className="flex items-center gap-2">
                                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => setConfirmModal(true)} className="p-3 bg-green-500 hover:bg-green-600 text-white rounded-2xl shadow-lg shadow-green-500/30 transition-all"><Save size={20} /></motion.button>
                                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => setEditRowId(null)} className="p-3 bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 rounded-2xl hover:bg-gray-200 dark:hover:bg-white/20 transition-all"><RotateCcw size={20} /></motion.button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-4 group-hover:translate-x-0">
                                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => { setEditRowId(times.Id); setEditData({ ...times, StartTime: moment(times.StartTime).format("HH:mm"), EndTime: moment(times.EndTime).format("HH:mm") }); }} className="p-3 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-2xl transition-all"><Edit3 size={20} /></motion.button>
                                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => { setSelectedBreakId(times.Id); setIsModalOpen(true); }} className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-2xl transition-all"><Trash2 size={20} /></motion.button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-24 flex flex-col items-center text-center">
                      <div className="w-24 h-24 bg-gray-50 dark:bg-white/[0.02] rounded-[2rem] flex items-center justify-center mb-6">
                        <Activity className="w-10 h-10 text-gray-200 dark:text-gray-800" />
                      </div>
                      <h4 className="text-2xl font-black text-gray-900 dark:text-white">Empty Session History</h4>
                      <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-xs font-medium">No manual or automatic break records have been detected for this member session yet.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-8 border-t dark:border-white/5 bg-gray-50/30 dark:bg-[#0A0A0A] flex justify-center">
                <button
                  onClick={toggleModal}
                  className="px-12 py-4 bg-gray-900 dark:bg-white text-white dark:text-black font-black rounded-3xl text-sm shadow-xl shadow-gray-900/10 dark:shadow-none hover:scale-105 active:scale-95 transition-all duration-500 uppercase tracking-widest"
                >
                  Finalize Log Manager
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Confirmation System */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsModalOpen(false)} className="absolute inset-0 bg-black/70 backdrop-blur-xl" />
            <motion.div initial={{ scale: 0.8, opacity: 0, y: 40 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.8, opacity: 0, y: 40 }} className="relative bg-white dark:bg-gray-900 p-10 rounded-[3rem] shadow-3xl max-w-sm w-full text-center border border-gray-100 dark:border-white/10">
              <div className="w-24 h-24 bg-red-500/10 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 animate-bounce">
                <Trash2 size={44} className="text-red-500" />
              </div>
              <h3 className="text-2xl font-black dark:text-white mb-3">Terminate Record?</h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-10 leading-relaxed px-4">Warning: This action is permanent and will remove the session data from all global reports.</p>
              <div className="grid grid-cols-1 gap-3">
                <button onClick={() => handleDeleteBreak(selectedBreakId)} disabled={isLoading} className="h-14 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-black shadow-xl shadow-red-600/20 transition-all flex items-center justify-center gap-3">
                  {isLoading ? <RotateCcw size={20} className="animate-spin" /> : "Destroy Record"}
                </button>
                <button onClick={() => setIsModalOpen(false)} className="h-14 bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 rounded-2xl font-black hover:bg-gray-200 dark:hover:bg-white/10 transition-all">Keep Session</button>
              </div>
            </motion.div>
          </div>
        )}

        {confirmModal && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setConfirmModal(false)} className="absolute inset-0 bg-black/70 backdrop-blur-xl" />
            <motion.div initial={{ scale: 0.8, opacity: 0, y: 40 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.8, opacity: 0, y: 40 }} className="relative bg-white dark:bg-gray-900 p-10 rounded-[3rem] shadow-3xl max-w-sm w-full text-center border border-gray-100 dark:border-white/10">
              <div className="w-24 h-24 bg-blue-500/10 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8">
                <Save size={44} className="text-blue-600" />
              </div>
              <h3 className="text-2xl font-black dark:text-white mb-3">Save Modifications?</h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-10 leading-relaxed px-4">Verify the refined time intervals and session classifications before committing.</p>
              <div className="grid grid-cols-1 gap-3">
                <button onClick={handleUpdateBreak} disabled={isLoading} className="h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black shadow-xl shadow-blue-500/20 transition-all flex items-center justify-center gap-3">
                  {isLoading ? <RotateCcw size={20} className="animate-spin" /> : "Update Data"}
                </button>
                <button onClick={() => setConfirmModal(false)} className="h-14 bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 rounded-2xl font-black hover:bg-gray-200 dark:hover:bg-white/10 transition-all">Discard Changes</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
